import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flag, Loader2, RotateCcw } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { StatsBar } from "@/components/StatsBar";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";

// Admin overview: campaign stats and charts. Pledge/MR/link management live
// on their own pages (see AdminLayout nav).
export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [presence, setPresence] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  // ----- Record finale control -----
  const [campaign, setCampaign] = useState(null);
  const [finaleBusy, setFinaleBusy] = useState(false);

  const loadCampaign = () => api.get("/campaign-state").then((r) => setCampaign(r.data)).catch(() => {});
  useEffect(() => {
    loadCampaign();
    const t = setInterval(loadCampaign, 10000);
    return () => clearInterval(t);
  }, []);

  const completeRecord = async () => {
    const typed = window.prompt(
      "This starts the 10→0 countdown on the live wall and CLOSES new entries when it reaches 0.\n\nType COMPLETE to confirm:"
    );
    if (typed !== "COMPLETE") { if (typed !== null) toast.error("Not confirmed — type COMPLETE exactly."); return; }
    setFinaleBusy(true);
    try {
      await api.post("/admin/complete-record");
      toast.success("Finale started — watch the live wall!");
      loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the finale");
    } finally {
      setFinaleBusy(false);
    }
  };

  const reopenRecord = async () => {
    if (!window.confirm("Reopen entries and clear the finale/completed state?")) return;
    setFinaleBusy(true);
    try {
      await api.post("/admin/reopen-record");
      toast.success("Entries reopened");
      loadCampaign();
    } catch {
      toast.error("Could not reopen");
    } finally {
      setFinaleBusy(false);
    }
  };

  // Live count of users currently on a pledge form (safe-to-deploy check).
  useEffect(() => {
    const loadPresence = () => api.get("/admin/presence").then((r) => setPresence(r.data)).catch(() => {});
    loadPresence();
    const t = setInterval(loadPresence, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <AdminLayout>
      <div
        data-testid="presence-card"
        className={`rounded-2xl border p-5 flex flex-wrap items-center gap-4 ${
          (presence?.count ?? 0) > 0 ? "border-amber-300 bg-amber-50" : "border-green-200 bg-green-50"
        }`}
      >
        <span className={`relative flex h-3 w-3 ${(presence?.count ?? 0) > 0 ? "" : "opacity-60"}`}>
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${(presence?.count ?? 0) > 0 ? "bg-amber-500" : "bg-green-500"}`} />
          <span className={`relative inline-flex rounded-full h-3 w-3 ${(presence?.count ?? 0) > 0 ? "bg-amber-500" : "bg-green-500"}`} />
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="font-display font-extrabold text-2xl text-slate-900 leading-none">
            {presence ? presence.count : "…"} <span className="text-sm font-semibold text-slate-500">user{presence?.count === 1 ? "" : "s"} on pledge forms right now</span>
          </p>
          <p className="text-xs text-slate-500 mt-1.5">
            {(presence?.count ?? 0) > 0
              ? "Someone is mid-form — hold the deploy for a few minutes."
              : "No one is filling a form — safe to deploy."}
            {presence && Object.keys(presence.by_page || {}).length > 0 && (
              <span className="ml-2 text-slate-400">
                ({Object.entries(presence.by_page).map(([k, v]) => `${k}: ${v}`).join(" · ")})
              </span>
            )}
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">updates every 10s</span>
      </div>

      {/* Record finale control */}
      <div
        data-testid="finale-card"
        className={`rounded-2xl border p-5 flex flex-wrap items-center justify-between gap-4 ${
          campaign?.record_completed ? "border-[#138808] bg-green-50" : campaign?.finale_started_at ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"
        }`}
      >
        <div className="min-w-[260px]">
          <p className="font-display font-bold text-slate-900 flex items-center gap-2">
            <Flag className="w-5 h-5 text-[#FF9933]" /> Record Finale
          </p>
          <p className="text-sm text-slate-600 mt-1">
            {campaign?.record_completed
              ? <>Record completed — final count <span className="font-display font-extrabold text-[#138808]">{(campaign.final_count ?? 0).toLocaleString("en-IN")}</span>. New entries are closed.</>
              : campaign?.finale_started_at
                ? "Countdown running on the live wall — entries close when it reaches 0."
                : "Starts the 10→0 countdown on the live wall (5s per number, fireworks). Entries close at 0 and the final number stays on screen."}
          </p>
        </div>
        {campaign?.record_completed || campaign?.finale_started_at ? (
          <Button data-testid="reopen-record-btn" onClick={reopenRecord} disabled={finaleBusy} variant="outline" className="rounded-full border-slate-300">
            {finaleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-2" /> Reopen entries</>}
          </Button>
        ) : (
          <Button data-testid="complete-record-btn" onClick={completeRecord} disabled={finaleBusy} className="rounded-full bg-[#FF9933] hover:bg-orange-500 text-slate-900 font-bold px-6">
            {finaleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Flag className="w-4 h-4 mr-2" /> Complete Record</>}
          </Button>
        )}
      </div>

      <StatsBar stats={stats} />

      {analytics && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4">Top States</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics.states.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0056D2" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <h3 className="font-display font-bold text-slate-900 mb-4">Top Cities</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics.cities.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#FF9933" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
